import React, { useState, useMemo, useCallback } from 'react';
import { _ } from '@splunk/ui-utils/i18n';
import { useSplunkTheme } from '@splunk/themes';
import Menu from '@splunk/react-ui/Menu';
import Popover from '@splunk/react-ui/Popover';
import {
    useSelector,
    selectTabs,
    selectActiveTab,
} from '@splunk/dashboard-state';
import { usePopoverBlurCallback } from './hooks';

const menuStyle = { width: 240, maxHeight: 650 };

// There is no direct way to reduce the padding on a Menu.Heading. In prisma, this looks odd.
//  Instead, we set a negative margin on the child heading element that counteracts the padding
const prismaHeadingStyle = { margin: '-7px 0px' };

export interface TabListMenuType {
    open: boolean;
    onRequestClose: () => void;
    anchor: HTMLButtonElement | null;
    onSelectTab: (layoutId: string) => void;
    showActiveTab?: boolean;
    heading?: string;
    tabListMenuId: string;
}

export const TabListMenu = ({
    open,
    onRequestClose,
    anchor,
    onSelectTab,
    showActiveTab = false,
    heading,
    tabListMenuId,
}: TabListMenuType): JSX.Element | null => {
    const tabs = useSelector(selectTabs);
    const activeTab = useSelector(selectActiveTab);
    const { family } = useSplunkTheme();

    const headingStyle = family === 'prisma' ? prismaHeadingStyle : undefined;

    const onBlur = usePopoverBlurCallback(onRequestClose);

    // this still generates a new function on every call, but it's within a useMemo so it's fine
    const handleClickGenerator = useCallback(
        (layoutId: string) => () => onSelectTab(layoutId),
        [onSelectTab]
    );

    const menuItems = useMemo(() => {
        return tabs.reduce<JSX.Element[]>((items, tab) => {
            // if we're hiding the active tab
            if (!showActiveTab && tab.layoutId === activeTab) {
                return items;
            }

            const menuItem = (
                <Menu.Item
                    onClick={handleClickGenerator(tab.layoutId)}
                    key={tab.layoutId}
                    description={
                        tab.layoutId === activeTab
                            ? _('Current tab')
                            : undefined
                    }
                >
                    {tab.label?.trim() || _(`Unnamed tab`)}
                </Menu.Item>
            );

            if (tab.layoutId === activeTab) {
                // active tab + divider always render as first item
                items.unshift(
                    menuItem,
                    <Menu.Divider key="active-tab-divider" />
                );
            } else {
                items.push(menuItem);
            }

            return items;
        }, []);
    }, [tabs, showActiveTab, activeTab, handleClickGenerator]);

    // only return if there are no menu options
    if (tabs.length <= 0 || (tabs.length === 1 && !showActiveTab)) {
        return null;
    }

    return (
        <Popover
            open={open}
            anchor={anchor}
            onRequestClose={onRequestClose}
            onBlur={onBlur}
            defaultPlacement="right"
            appearance="normal"
            takeFocus
            id={tabListMenuId}
        >
            <Menu
                style={menuStyle}
                data-test="tab-list-menu"
                aria-label={heading}
            >
                {!!heading && (
                    <Menu.Heading title outerStyle={headingStyle}>
                        {heading}
                    </Menu.Heading>
                )}
                {!!heading && <Menu.Divider />}
                {menuItems}
            </Menu>
        </Popover>
    );
};

export const useTabListMenu = () => {
    const [open, setOpen] = useState(false);
    const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);

    const handleOpen = useCallback(() => {
        setOpen(true);
    }, []);

    const handleRequestClose = useCallback(() => {
        setOpen(false);
        anchor?.focus();
    }, [anchor]);

    const tabCount = useSelector(selectTabs).length;

    return {
        handleOpen,
        handleRequestClose,
        setRef: setAnchor,
        open,
        anchor,
        tabCount,
    };
};
